import React from 'react'
import papa from 'papaparse'
import { useLazyQuery } from '@apollo/client'
import { useHistory } from 'react-router-dom'
import {
  TestSessionStatus,
  useTestSessionsData,
  useAuth,
  UserRole,
  getFormattedDateAndTime,
  Lab,
  useStateWithDebounce,
  TestSessions_testSessions_testSessions,
  TestSessionsVariables as TestSessionsVariablesCsv,
  TestSessions as TestSessionsCsv,
  SortDirectionEnum,
  TestSessionsVariables,
  CompanyMemberRole,
  GroupMemberRole,
  UserPermissions,
  Me_me_User_UserRoles,
} from '@modmd/data'
import { Downloading } from 'components/Downloading'
import { useSortChangeHandler } from 'utils/helpers'
import { Box, Container } from 'components/Layout'
import Button from 'components/Button'
import Icon from 'components/Icon'
import { SelectInput } from 'components/SelectInput'
import { DateInput } from 'components/DateInput'
import TextInput from 'components/TextInput'
import { TestSessionsDatalist } from 'sharedComponents/TestSessionsDatalist'
import DashboardPage from 'internal-portal/sharedComponents/DashboardPage'
import { useSearchState } from 'internal-portal/utils/useSearchState'
import { ROUTES } from 'internal-portal/constants/routes'
import { RESULTS_PER_PAGE } from 'internal-portal/constants/filters'
import { endOfDay, startOfDay } from 'date-fns'
import { useStatuses } from 'utils/hooks/useStatuses'
import { filterStatus, LAB_OPTIONS } from './utils'
import { TEST_SESSIONS_CSV } from './operations'

export enum FormName {
  DATE_FROM = 'dateFrom',
  DATE_TO = 'dateTo',
  STATUS = 'status',
  COMPANY_OR_GROUP = 'companyOrGroup',
  USER = 'userNameOrEmail',
  LAB = 'lab',
  RESULTS_PER_PAGE = 'resultsPerPage',
}

export enum PaginationProps {
  PAGE = 'page',
}

const TestSessions = () => {
  const history = useHistory()
  const searchState = useSearchState<{
    [FormName.DATE_TO]?: string
    [FormName.DATE_FROM]?: string
    [FormName.STATUS]?: TestSessionStatus | null
    [FormName.USER]?: string
    [FormName.COMPANY_OR_GROUP]?: string
    [FormName.LAB]?: Lab | null
    [PaginationProps.PAGE]?: string
    [FormName.RESULTS_PER_PAGE]?: string
  }>()
  const {
    hasRoles,
    getUserCompaniesWithRole,
    getUserGroupsWithRole,
    hasPermission,
    data: userData,
  } = useAuth()
  const { getStatuses } = useStatuses()
  const isSuperAdmin = hasRoles([UserRole.SUPER_ADMIN])
  const isCLS = hasRoles([UserRole.CLS])
  const isAdmin = hasRoles([UserRole.ADMIN])
  const isResults = hasRoles([UserRole.RESULTS])
  const isNurse = hasRoles([UserRole.NURSE])
  const isPatient = hasRoles([UserRole.PATIENT])
  const isScheduler = hasRoles([UserRole.SCHEDULER])
  const isLiaison = hasRoles([UserRole.LIAISON]) && userData.User.UserRoles.length === 1
  const [download, setDownload] = React.useState(false)
  const [downloadableDataUrl, setDownloadableDataUrl] = React.useState<string>()
  const { status, dateTo, dateFrom, page, lab } = searchState.data
  const [userNameOrEmail, setUserNameOrEmail, debouncedUserNameOrEmail] = useStateWithDebounce(
    '',
    (userNameOrEmail) => {
      searchState.setSearchState({ userNameOrEmail, page: 1 }, false, true)
    }
  )
  const [companyOrGroup, setCompanyOrGroup, debouncedCompanyOrGroup] = useStateWithDebounce(
    '',
    (companyOrGroup) => {
      searchState.setSearchState({ companyOrGroup, page: 1 }, false, true)
    }
  )

  const { companies, hasCompaniesWithRole } = getUserCompaniesWithRole({
    roles: [CompanyMemberRole.Manager, CompanyMemberRole.Owner],
  })
  const { groups, hasGroupsWithRole } = getUserGroupsWithRole({
    roles: [GroupMemberRole.Coordinator, GroupMemberRole.Manager],
  })

  const getCompanyIds = () => {
    if (
      !hasPermission([UserPermissions.SCHEDULE_TEST_SESSION_ALL_USERS]) &&
      (hasPermission([UserPermissions.SEE_GROUP_COMPANY_TEST_SESSIONS]) ||
        hasPermission([UserPermissions.SEE_FINISHED_TEST_SESSIONS_GROUP_ASSIGNED]))
    )
      return userData.User.CompaniesWithRole.length
        ? userData.User.CompaniesWithRole.map(({ id }) => id)
        : undefined
    if (!hasPermission([UserPermissions.SCHEDULE_TEST_SESSION_ALL_USERS]))
      return companies.length ? companies.map(({ id }) => id) : undefined
    return undefined
  }

  const getGroupIds = () => {
    if (!companies.length) {
      if (
        !hasPermission([UserPermissions.SCHEDULE_TEST_SESSION_ALL_USERS]) &&
        (hasPermission([UserPermissions.SEE_GROUP_COMPANY_TEST_SESSIONS]) ||
          hasPermission([UserPermissions.SEE_FINISHED_TEST_SESSIONS_GROUP_ASSIGNED]))
      )
        return userData.User.GroupsWithRole.length
          ? userData.User.GroupsWithRole.map(({ id }) => id)
          : undefined
      if (!hasPermission([UserPermissions.SCHEDULE_TEST_SESSION_ALL_USERS]))
        return groups.length ? groups.map(({ id }) => id) : undefined
      return undefined
    }
    return undefined
  }

  const { handleSortChange, sorter } = useSortChangeHandler()
  const testSessionsQuery = useTestSessionsData({
    filter: Object.entries({
      lab,
      statuses: status ? [status] : getStatuses(),
      ...(isPatient && (hasCompaniesWithRole || hasGroupsWithRole) && { statuses: status }),
      dateTo,
      ...(!isNurse && !isAdmin && { companyIds: getCompanyIds() }),
      ...(!isNurse && !isAdmin && { groupIds: getGroupIds() }),
      ...(isLiaison && {
        companyIds: companies.length ? companies.map(({ id }) => id) : [0],
        groupIds: groups.length ? groups.map(({ id }) => id) : [0],
      }),
      dateFrom,
      companyOrGroup: debouncedCompanyOrGroup,
      userNameOrEmail: debouncedUserNameOrEmail,
    }).reduce((acc, [key, value]) => (value ? { ...acc, [key]: value } : acc), {}),
    page: Number(page || 1),
    sorter,
    pageLength:
      Number(searchState.data[FormName.RESULTS_PER_PAGE]) || Number(RESULTS_PER_PAGE[0].value),
  })

  const handleFilterChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const { value, name } = event.target
      searchState.setSearchState({ [name]: value, page: 1 }, false, true)
    },
    [searchState]
  )

  const getSessionStatus = (session: TestSessions_testSessions_testSessions) => {
    if (session.finishedAt) {
      return 'Finished'
    }
    if (session.rejectedAt) {
      return 'Rejected'
    }
    if (session.isScheduled) {
      return 'Scheduled'
    }
    return 'Requested'
  }

  const [getSessionsCsv, { data: sessionsCsv, loading: sessionsCsvLoading }] = useLazyQuery<
    TestSessionsCsv,
    TestSessionsVariablesCsv
  >(TEST_SESSIONS_CSV)

  React.useEffect(() => {
    if (download) {
      const sessionResult = sessionsCsv?.testSessions.testSessions.map(
        (session: TestSessions_testSessions_testSessions) => {
          const {
            id,
            date,
            name,
            lab,
            type,
            Location,
            sublocation,
            Members,
            Company,
            Groups,
            paymentStatus,
            Invoice,
          } = session
          const sessionDate = getFormattedDateAndTime(date)
          const sessionTime = getFormattedDateAndTime(date, true, false)
          const location = `${Location.street}, ${Location.city}`
          const numOfPeopleScheduled = Members.filter(({ isParticipant }) => isParticipant).length
          const numOfPeopleTested = Members.filter(
            ({ isParticipant, isResulted }) => isParticipant && isResulted
          ).length
          const company = Company?.name || ''
          const groups = Groups.map((group) => ` ${group.id}-${group.name}`)
          const sessionStatus = getSessionStatus(session)

          const sessionResult = {
            sessionId: id,
            sessionDate,
            sessionTime,
            sessionName: name,
            type,
            lab,
            location,
            'Rate type': sublocation,
            numOfPeopleScheduled,
            numOfPeopleTested,
            company,
            groups,
            sessionStatus,
            paymentStatus: paymentStatus || 'Invoicing',
            invoiceDocNumber: Invoice?.invoiceDocNumber,
          }
          return sessionResult
        }
      )
      if (sessionResult && !downloadableDataUrl) {
        const csv = papa.unparse(sessionResult, { header: true })
        const dataBlob = new Blob([csv], { type: 'text/csv' })

        const link = document.createElement('a')
        link.href = window.URL.createObjectURL(dataBlob)
        link.setAttribute('download', `Tests-Results-${Date()}.csv`)
        document.body.appendChild(link)
        link.click()
        setDownload(false)
      } else if (downloadableDataUrl) {
        window.URL.revokeObjectURL(downloadableDataUrl)
        setDownloadableDataUrl('')
        setDownload(false)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionsCsv])

  const queryVariables = React.useMemo<TestSessionsVariables>(
    () => ({
      filterData: {
        dateBetween: [dateFrom, dateTo],
        lab: lab || null,
        company: companyOrGroup
          ? {
              nameOrGroupNameLike: companyOrGroup,
            }
          : null,
        user: userNameOrEmail
          ? {
              emailOrNameLike: userNameOrEmail,
            }
          : null,
        statuses: status ? [TestSessionStatus[status]] : undefined,
      },
      pagination: {
        from: 0,
        length: testSessionsQuery.data?.testSessions.pagination.total || 10,
        sortBy: 'date',
        direction: SortDirectionEnum.DESC,
      },
      gmt: new Date().getTimezoneOffset(),
    }),
    [
      companyOrGroup,
      dateFrom,
      dateTo,
      lab,
      status,
      testSessionsQuery.data?.testSessions.pagination.total,
      userNameOrEmail,
    ]
  )

  const handleDownloadCsv = () => {
    getSessionsCsv({
      variables: queryVariables,
    })
    setDownload(true)
  }

  return (
    <DashboardPage
      Header={
        (isSuperAdmin || isCLS || isScheduler || isResults) && (
          <>
            <Box display="flex" justifyContent="flex-end" p="1rem">
              {hasPermission([UserPermissions.SCHEDULE_TEST_SESSION_ALL_USERS]) && (
                <Button
                  onClick={() => history.push(`${ROUTES.REQUEST_TEST_SESSION}`)}
                  leftIcon={<Icon.Plus size="L" />}
                  marginRight="0.25rem"
                >
                  Schedule
                </Button>
              )}
              <Button appearance="filled" onClick={handleDownloadCsv} disabled={sessionsCsvLoading}>
                {sessionsCsvLoading ? <Downloading /> : 'Download csv'}
              </Button>
            </Box>
          </>
        )
      }
    >
      <Container>
        <TestSessionsDatalist
          columnLabels={{ invoiceCheck: null }}
          tableHeader={
            <Box
              display="grid"
              gridTemplateColumns={{
                TABLET: 'repeat(2, 1fr)',
                DESKTOP: 'repeat(5, 1fr)',
              }}
              gridGap="1rem"
            >
              <DateInput
                id="dateFrom"
                name={FormName.DATE_FROM}
                label="Date From"
                value={
                  searchState.data.dateFrom
                    ? new Date(searchState.data[FormName.DATE_FROM] as string)
                    : null
                }
                onChange={(date) => {
                  searchState.setSearchState(
                    {
                      [FormName.DATE_FROM]: date ? startOfDay(date).toISOString() : null,
                      page: 1,
                    },
                    false,
                    true
                  )
                }}
                isFullWidth
                hasBottomContent
              />
              <DateInput
                id="dateTo"
                name={FormName.DATE_TO}
                label="Date To"
                value={
                  searchState.data.dateTo
                    ? new Date(searchState.data[FormName.DATE_TO] as string)
                    : null
                }
                onChange={(date) => {
                  searchState.setSearchState(
                    {
                      [FormName.DATE_TO]: date ? endOfDay(date).toISOString() : null,
                      page: 1,
                    },
                    false,
                    true
                  )
                }}
                isFullWidth
              />
              <SelectInput
                name={FormName.STATUS}
                label="Session status"
                value={searchState.data[FormName.STATUS] || ''}
                onChange={handleFilterChange}
                options={filterStatus(
                  userData.User.UserRoles?.map((user: Me_me_User_UserRoles) => user.role!)
                )}
                hasBottomContent
              />
              <SelectInput
                name={FormName.LAB}
                label="Lab"
                value={searchState.data[FormName.LAB] || ''}
                onChange={handleFilterChange}
                options={LAB_OPTIONS}
                hasBottomContent
              />
              <TextInput
                name={FormName.USER}
                label="User"
                placeholder="Name or email"
                value={userNameOrEmail}
                onChange={(event) => setUserNameOrEmail(event.target.value)}
                hasBottomContent
              />
              <TextInput
                name={FormName.COMPANY_OR_GROUP}
                label="Company or Group"
                value={companyOrGroup}
                onChange={(event) => setCompanyOrGroup(event.target.value)}
                hasBottomContent
              />
              <SelectInput
                name="resultsPerPage"
                label="Results per page"
                value={searchState.data[FormName.RESULTS_PER_PAGE] || ''}
                onChange={handleFilterChange}
                options={RESULTS_PER_PAGE}
                hasBottomContent
              />
            </Box>
          }
          testSessions={testSessionsQuery.data?.testSessions?.testSessions || []}
          pagination={testSessionsQuery.data?.testSessions?.pagination}
          isLoading={testSessionsQuery.loading}
          onSortChange={handleSortChange}
          sorter={sorter}
          onPageChange={(page) => searchState.setSearchState({ page }, false)}
          onRowClick={({ id }) => history.push(`/${ROUTES.SESSIONS}/${id}`)}
          pageLength={testSessionsQuery.data?.testSessions?.pagination.length}
        />
      </Container>
    </DashboardPage>
  )
}

export default TestSessions
